<template>
  <div>
    <!-- Loader -->
    <div v-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
      <div class="mt-2">{{ $t("loading") }}...</div>
    </div>

    <!-- Custom tabela sa gruppovanim headers -->
    <div v-else-if="!loading && groupedData.length > 0" class="table-wrapper">
      <table class="v-data-table striped-table ordera-box-shadow" style="width: 100%">
        <!-- Glavni header red sa kategorijama -->
        <thead>
          <tr class="category-header">
            <th rowspan="2" class="text-center day-header">{{ $t("day") }}</th>
            <th
              v-for="category in filteredMealCategories"
              :key="category"
              :colspan="visibleShiftsCount + 1"
              class="text-center category-cell"
            >
              {{ toLatin(categoryLabel(category)) }}
            </th>
            <th rowspan="2" class="text-center grand-total-header">{{ $t("total") }}</th>
          </tr>
          <tr class="shift-header">
            <template v-for="category in filteredMealCategories">
              <th
                v-for="shift in [1, 2, 3, 4]"
                :key="`${category}_${shift}`"
                class="text-center shift-cell"
                :class="{ 'hidden-shift': !selectedShift.includes(shift) }"
              >
                {{ shift === 1 ? "I" : shift === 2 ? "II" : shift === 3 ? "III" : "IV" }}
              </th>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <th
                    v-on="on"
                    :key="`${category}_total`"
                    class="text-center total-shift-cell"
                  >
                    <span v-bind="attrs">Σ</span>
                  </th>
                </template>
                <span>{{ $t("total") }}</span>
              </v-tooltip>
            </template>
          </tr>
        </thead>
        <!-- Telo tabele -->
        <tbody>
          <tr
            v-for="item in groupedData"
            :key="item.day"
            :class="{ 'total-row': item.isTotal }"
          >
            <td class="text-center day-cell">
              <strong v-if="item.isTotal">{{ $t("total") }}</strong>
              <span v-else>{{ item.day }}</span>
            </td>
            <template v-for="category in filteredMealCategories">
              <td
                v-for="shift in [1, 2, 3, 4]"
                :key="`${category}_${shift}`"
                class="text-center data-cell"
                :class="{ 'hidden-shift': !selectedShift.includes(shift) }"
              >
                <strong v-if="item.isTotal">{{
                  item[`${category}_${shift}`] || 0
                }}</strong>
                <span v-else>{{ item[`${category}_${shift}`] || 0 }}</span>
              </td>
              <td :key="`${category}_total`" class="text-center total-data-cell">
                <strong v-if="item.isTotal">{{ item[`${category}_total`] || 0 }}</strong>
                <span v-else>{{ item[`${category}_total`] || 0 }}</span>
              </td>
            </template>
            <td class="text-center grand-total-cell">
              <strong v-if="item.isTotal">{{ item.grandTotal || 0 }}</strong>
              <strong v-else>{{ item.grandTotal || 0 }}</strong>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Poruka kada nema podataka -->
    <div v-else-if="!loading && groupedData.length === 0" class="text-center py-8">
      <v-icon size="48" color="grey">mdi-information-outline</v-icon>
      <div class="mt-2 grey--text">{{ $t("no_data_available") }}</div>
    </div>
  </div>
</template>

<script>
export default {
  name: "GroupedShiftMealsTable",
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    dailyData: {
      type: Object,
      required: true,
    },
    mealCategories: {
      type: Array,
      required: true,
    },
    selectedShift: {
      type: Array,
      required: true,
    },
    selectedCategories: {
      type: Array,
      default: () => [],
    },
    dateArray: {
      type: Array,
      required: true,
    },
    // Opciono: mapa ključ grupe (npr. category_id) -> naziv za prikaz.
    // Ako nije prosleđena, ključ se prikazuje kao naziv.
    categoryLabels: {
      type: Object,
      default: () => ({}),
    },
  },
  methods: {
    translate(key) {
      return this.$t(key) || key;
    },
    categoryLabel(category) {
      return this.categoryLabels[category] ?? category;
    },
    generateDateArray(startDate, endDate) {
      const dates = [];
      const start = new Date(startDate);
      const end = new Date(endDate);

      while (start <= end) {
        dates.push(start.toISOString().substr(0, 10));
        start.setDate(start.getDate() + 1);
      }

      return dates;
    },
  },
  computed: {
    categoryItems() {
      return this.mealCategories.map((category) => ({
        text: this.translate(category),
        value: category,
      }));
    },
    filteredMealCategories() {
      return this.selectedCategories && this.selectedCategories.length > 0
        ? this.selectedCategories
        : this.mealCategories;
    },
    shiftsToProcessForHeaders() {
      // Prikazuj samo selektovane smene
      return this.selectedShift && this.selectedShift.length > 0
        ? this.selectedShift
        : [1, 2, 3, 4];
    },
    visibleShiftsCount() {
      // Broj selektovanih smena za colspan kalkulaciju
      return this.selectedShift.length;
    },
    shiftsCount() {
      // Broj selektovanih smena
      return this.selectedShift.length;
    },
    headers() {
      const headers = [{ text: this.$t("day"), value: "day", align: "center" }];

      // Odredi koje smene treba da prikazujem u zavisnosti od filtera - sada je niz
      const shiftsToProcess =
        this.selectedShift && this.selectedShift.length > 0
          ? this.selectedShift
          : [1, 2, 3, 4];

      // Kreiraj header za svaku kombinaciju kategorija+smena
      this.filteredMealCategories.forEach((category) => {
        shiftsToProcess.forEach((shift) => {
          const combinationKey = `${category}_${shift}`;
          // Kraći format: "Kategorija I", "Kategorija II", "Kategorija III", "Kategorija IV"
          const displayName = `${this.translate(this.categoryLabel(category))} ${
            shift === 1 ? "I" : shift === 2 ? "II" : shift === 3 ? "III" : "IV"
          }`;

          headers.push({
            text: displayName,
            value: combinationKey,
            align: "center",
          });
        });

        // Dodaj total kolonu za svaku kategoriju
        headers.push({
          text: `${this.translate(this.categoryLabel(category))} ${this.$t("total")}`,
          value: `${category}_total`,
          align: "center",
        });
      });

      return headers;
    },
    groupedData() {
      const data = [];
      const shiftsToProcess =
        this.selectedShift && this.selectedShift.length > 0
          ? this.selectedShift
          : [1, 2, 3, 4];

      // Dodaj redove za svaki datum u range-u
      this.dateArray.forEach((date) => {
        // Izvuci dan iz datuma (npr. "2025-09-12" -> "12")
        const day = date.split("-")[2];

        const dayRow = {
          day: day, // Prikaži dan (01, 02, 03... 30)
          isTotal: false,
        };

        // Popuni podatke za svaku kombinaciju kategorija+smena
        let dayGrandTotal = 0;
        this.filteredMealCategories.forEach((category) => {
          let categoryTotal = 0;
          // Generiši podatke za sve smene (1,2,3,4) ali saberi samo selektovane
          [1, 2, 3, 4].forEach((shift) => {
            const combinationKey = `${category}_${shift}`;
            const value = this.dailyData[date]
              ? this.dailyData[date][combinationKey] || 0
              : 0;
            dayRow[combinationKey] = value;

            // Saberi u ukupno samo ako je smena selektovana
            if (shiftsToProcess.includes(shift)) {
              categoryTotal += value;
            }
          });
          // Dodaj ukupno za kategoriju (samo selektovane smene)
          dayRow[`${category}_total`] = categoryTotal;
          dayGrandTotal += categoryTotal;
        });

        // Dodaj ukupno za ceo dan (zbir svih kategorija)
        dayRow.grandTotal = dayGrandTotal;

        // Uvek dodaj red za svaki datum iz filtera, bez obzira na podatke
        data.push(dayRow);
      });

      // Dodaj ukupan red
      const totalRow = {
        day: this.$t("total"),
        isTotal: true,
      };

      this.filteredMealCategories.forEach((category) => {
        // Generiši totale za sve smene (1,2,3,4) ali saberi samo selektovane
        [1, 2, 3, 4].forEach((shift) => {
          const combinationKey = `${category}_${shift}`;
          totalRow[combinationKey] = data.reduce(
            (sum, row) => sum + (row[combinationKey] || 0),
            0
          );
        });

        // Add total column for the category (samo selektovane smene)
        const categoryTotalKey = `${category}_total`;
        totalRow[categoryTotalKey] = data.reduce(
          (sum, row) => sum + (row[categoryTotalKey] || 0),
          0
        );
      });

      // Dodaj grand total za total red (zbir svih kategorija u svim danima)
      totalRow.grandTotal = data.reduce((sum, row) => sum + (row.grandTotal || 0), 0);

      data.push(totalRow);

      return data;
    },
    printHeaders() {
      return this.headers;
    },
    printItems() {
      return this.groupedData.map((item) => {
        const printItem = { day: item.day };
        const shiftsToProcess =
          this.selectedShift && this.selectedShift.length > 0
            ? this.selectedShift
            : [1, 2, 3, 4];

        this.filteredMealCategories.forEach((category) => {
          shiftsToProcess.forEach((shift) => {
            const combinationKey = `${category}_${shift}`;
            printItem[combinationKey] = item[combinationKey] || 0;
          });

          // Add total column for the category
          const categoryTotalKey = `${category}_total`;
          printItem[categoryTotalKey] = item[categoryTotalKey] || 0;
        });

        return printItem;
      });
    },
  },
};
</script>

<style scoped>
.table-wrapper {
  overflow-x: auto;
}

.table-wrapper table {
  border-collapse: collapse;
  font-size: 14px;
}

.category-header th,
.shift-header th {
  background-color: rgba(0, 0, 0, 0.04);
  border: 1px solid #e0e0e0;
  padding: 12px 8px;
  font-weight: 600;
}

.category-cell {
  background-color: #e3f2fd !important;
  font-size: 16px;
  font-weight: 700;
}

.shift-cell {
  background-color: #f0f8ff !important;
  font-size: 14px;
  font-weight: 600;
  min-width: 60px;
}

.day-header {
  background-color: rgba(0, 0, 0, 0.04) !important;
  font-weight: 700;
  min-width: 80px;
}

.day-cell {
  background-color: rgba(0, 0, 0, 0.02);
  border: 1px solid #e0e0e0;
  padding: 8px;
  font-weight: 600;
  min-width: 80px;
}

.data-cell {
  border: 1px solid #e0e0e0;
  padding: 8px;
  min-width: 60px;
}

.total-row {
  background-color: #f0f0f0 !important;
  font-weight: 700;
}

.total-row .data-cell {
  background-color: #e8f5e8 !important;
  font-weight: 700;
}

.total-shift-cell {
  background-color: #fff3e0 !important;
  font-size: 14px;
  font-weight: 700;
  min-width: 60px;
  border: 1px solid #e0e0e0;
}

.total-data-cell {
  background-color: #fff3e0 !important;
  border: 1px solid #e0e0e0;
  padding: 8px;
  font-weight: 700;
  min-width: 60px;
}

.total-row .total-data-cell {
  background-color: #ffecb3 !important;
  font-weight: 800;
}

/* Alternating row colors */
tbody tr:nth-child(even):not(.total-row) {
  background-color: #f9f9f9;
}

tbody tr:nth-child(odd):not(.total-row) {
  background-color: #ffffff;
}

/* Hidden shift styling */
.hidden-shift {
  display: none !important;
}

/* Grand total column styling */
.grand-total-header {
  background-color: #e3f2fd !important;
  font-size: 16px;
  font-weight: 700;
  min-width: 80px;
  border: 1px solid #e0e0e0;
}

.grand-total-cell {
  background-color: #fff8e1 !important;
  border: 1px solid #ffecb3;
  padding: 8px;
  font-weight: 700;
  min-width: 80px;
  font-size: 15px;
}

.total-row .grand-total-cell {
  background-color: #ffecb3 !important;
  border: 1px solid #ffd54f;
  font-weight: 800;
  font-size: 16px;
}
</style>
